import React, { useContext } from 'react';
import { useQuery } from 'react-apollo';
import { useParams } from 'react-router-dom';
import { BasePage, Helmet, FourOhFourErrorPage, StaticContext, SurveyTheme } from '@sm/webassets';
import { t } from '@sm/intl';

import { GetCurrentSurveySessionQuery, GetCurrentSurveySessionQueryVariables } from '~lib/generatedGqlTypes';
import Survey from './Survey';
import setSurveyTakingDocumentTitle from './helper/surveyTakingWindowTitle';
import SEOBanner from '~app/components/SEOBanner';

import getCurrentSurveySession from './SurveyTaking.graphql';

import SurveyProvider from './SurveyContext';
import CurrentPageProvider from './CurrentPageContext';

const COPY = {
  META_DESCRIPTION: {
    id: 'SurveyTaking.metaDescription',
    defaultMessage:
      "Web survey powered by SurveyMonkey.com. Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
  },
  SEO_BANNER_CLOSE_BUTTON_ARIA_TEXT: {
    id: 'SurveyTaking.seoBannerCloseButtonAriaText',
    defaultMessage: 'close SEO banner',
  },
  SEO_BANNER_BODY: {
    id: 'SurveyTaking.seoBannerBody',
    defaultMessage: "Is this the survey you're looking for? Try creating your own with the world's leading platform.",
  },
  SEO_BANNER_SIGNUP_BUTTON: {
    id: 'SurveyTaking.seoBannerSignupButton',
    defaultMessage: 'Sign up free',
  },
  SEO_BANNER_LEARN_MORE: {
    id: 'SurveyTaking.seoBannerLearnMoreButton',
    defaultMessage: 'Learn more',
  },
};

const SurveyTaking: React.FC = () => {
  const {
    pageOptions: { isRequestFromSearchEngine, isWhiteLabel },
  } = useContext(StaticContext);

  const { collectorKey }: { collectorKey: string } = useParams();

  const {
    loading: loadingSessionData,
    error: errorSessionData,
    data: currentSurveySessionData,
  } = useQuery<GetCurrentSurveySessionQuery, GetCurrentSurveySessionQueryVariables>(getCurrentSurveySession, {
    variables: {
      collectionKey: collectorKey,
    },
  });

  if (errorSessionData) {
    throw errorSessionData;
  }

  if (loadingSessionData) {
    // Placeholder - replace with proper component when ready
    return <span>loading...</span>;
  }

  if (!currentSurveySessionData?.surveyTakingSession?.survey) {
    return <FourOhFourErrorPage />;
  }

  // seo banner config
  const seoBannerCallToActionButtons = [
    {
      label: t(COPY.SEO_BANNER_SIGNUP_BUTTON),
      uri: 'https://www.surveymonkey.com/user/sign-up/?ut_source=s_page_search_topbar_desktop',
    },
    {
      label: t(COPY.SEO_BANNER_LEARN_MORE),
      uri: 'https://www.surveymonkey.com/mp/take-a-tour/?ut_source=s_page_search_topbar_desktop',
    },
  ];

  if (currentSurveySessionData) {
    const {
      surveyTakingSession: {
        survey: {
          id: surveyId,
          language: { code: langCode },
        },
        currentPage,
        collector,
      },
    } = currentSurveySessionData;

    const { surveyTakingSession } = currentSurveySessionData;
    const surveyTitle = surveyTakingSession.survey?.title;
    const isTitleEnabled = surveyTakingSession.survey?.design?.surveyTitle.enabled ?? true;

    return (
      <BasePage
        actionFlow="SurveyTaking"
        pageId="SurveyTaking"
        legacyWeb="respweb"
        // are the following from survey design response?
        includeHeader={false}
        includeFooter={false}
        includeExperiments={false}
        pageMetricsAttributes={null}
      >
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{setSurveyTakingDocumentTitle(isTitleEnabled, isWhiteLabel, surveyTitle)}</title>
          <meta name="description" content={t(COPY.META_DESCRIPTION)} />
          <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
          <meta httpEquiv="content-language" content={langCode} />
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="surveyTaking">
          <SurveyProvider survey={surveyTakingSession.survey} currentPage={currentPage} collector={collector}>
            <SEOBanner
              callToActionButtons={seoBannerCallToActionButtons}
              closeButtonAriaText={t(COPY.SEO_BANNER_CLOSE_BUTTON_ARIA_TEXT)}
              requestFromSearchEngine={isRequestFromSearchEngine}
              collectorId=""
            >
              {t(COPY.SEO_BANNER_BODY)}
            </SEOBanner>
            <CurrentPageProvider>
              <Survey />
            </CurrentPageProvider>
          </SurveyProvider>
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
};

export default SurveyTaking;
